import { constantsSession } from "../constants/constants";
import CryptoJS from "crypto-js";
import { CRYPTOJS_SECRETKEY, CREDENTIALS_KEY } from "../constants/host";
import JSEncrypt from "jsencrypt";

// Los unicos valores que serán encriptados pertenecen a las keys contenidas en este array.
const constantsToEncrypt = [
  constantsSession.ACCESS_TOKEN,
  constantsSession.ACCESS_TOKEN_DATE,
  constantsSession.ACCESS_TOKEN_EXPIRE_IN,
  constantsSession.REFRESH_TOKEN,
  constantsSession.USER_INFO,
  constantsSession.ROLES_ME,
  constantsSession.ROLES_PERMISSIONS,
  constantsSession.PROVIDER_NAME
];

// Función que devuelve encriptado el valor recibido
export const toEncrypt = (value, name, isObject) => {
  // Validamos que el name no sea una excepcion en "encryptionExceptions"
  const hasToBeEncrypted = constantsToEncrypt.some(
    (constant) => constant === name
  );

  // Si no es una excepcion
  if (hasToBeEncrypted) {
    // Si es un objeto, lo parseamos a JSON
    const plainValue = isObject ? JSON.stringify(value) : String(value);

    // Encriptamos y devolvemos el valor encriptado
    return CryptoJS.AES.encrypt(
      plainValue,
      CRYPTOJS_SECRETKEY
    ).toString();
  }

  // Si no requiere encriptado, devolvemos el valor que recibimos
  return isObject ? JSON.stringify(value) : value;
};

// Función que devuelve desencriptado el valor recibido
export const toDecrypt = (value, name, isObject) => {
  // Validamos si name es una de las constantes que hay que desencriptar
  const hasToBeDecrypted = constantsToEncrypt.some(
    (constant) => constant === name
  );

  // Si requiere desencriptado
  if (hasToBeDecrypted) {
    // Desencriptamos y devolvemos el valor desencriptado
    const bytes = CryptoJS.AES.decrypt(value, CRYPTOJS_SECRETKEY);
    const plainValue = bytes.toString(CryptoJS.enc.Utf8);
    return isObject ? JSON.parse(plainValue) : plainValue;
  }

  // Si no requiere desencriptado, devolvemos el valor que recibimos
  return isObject ? JSON.parse(value) : value;
};

/**
 * Este método se utiliza para encriptar las credenciales del usuario
 * al momento del login.
 * 
 * @param {String} text Text to encrypt
 * @returns {String} Encrypted text
 */
export const encryptCredentials = (text) => {
  const key = CryptoJS.enc.Utf8.parse(CREDENTIALS_KEY);
  const iv = CryptoJS.enc.Utf8.parse(CREDENTIALS_KEY);

  const encryptedpassword = CryptoJS.AES.encrypt(CryptoJS.enc.Utf8.parse(text), key, {
    keySize: 128 / 8,
    iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7
  });

  return encryptedpassword.toString();
};

/**
 * Formatea una clave pública en formato PEM, añadiendo los saltos de línea y encabezados.
 * @param {string} rawKey La clave pública sin formato.
 * @returns {string} La clave en formato PEM.
 */
export const formatPublicKey = (rawKey) => {
  // Asegúrate de que la clave no tenga espacios en blanco
  const keyWithoutWhitespace = rawKey.trim();

  // Divide la clave en bloques de 64 caracteres y añade saltos de línea
  let formattedKey = '';

  for (let i = 0; i < keyWithoutWhitespace.length; i += 64) {
    formattedKey += `${keyWithoutWhitespace.substring(i, i + 64)}\n`;
  }

  // Agrega los encabezados y pie de página del formato PEM
  return `-----BEGIN PUBLIC KEY-----\n${formattedKey}-----END PUBLIC KEY-----`;
};

export const encryptWithRSA = (text) => {
  const credentialsKeyPublic =
    'MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAqRKCyJAZpWyKr7ro12ZqBMgW18UZlK7jv+EVBloPlaxWSmB4ITXj7/u5ZoZclJzNqVQ2OU+4R4QfuPDZPn5uTeRRzJoK46o6DUP792R3CVfuZdWPzLMUMwTPR4YoL9rU7YVY9g54G2n/gooh3BmNoYAG0tu59/6q27fSe9Twl2IUCff0QLAn1peOiVwsrQLnzxWtOelcTDh7nQe+9qn4h5HGGKmFxqzna2y0u8p8qMIWDa87Qc9YxVzzAcMZDDCtUp5Jpa+hZaj2WOPDY3A2EJqKlsh7KjSR8OC+O899DJ1HPiFu+pqUDeH3cJhwZ4vLRvsFrdjK5jGCw6VqD2NEtQIDAQAB';
  const crypt = new JSEncrypt({ default_key_size: 2048 });
  const publicKey = formatPublicKey(credentialsKeyPublic);

  crypt.setPublicKey(publicKey);
  const encriptedText = crypt.encrypt(text);

  if (encriptedText === false) throw new Error('Error al encriptar. Verifique la clave pública');

  return encriptedText;
};
